import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApplePay, GooglePay } from 'adyen-web-new';

import { useApplicationBuildContext } from '@context/ApplicationBuildContext';
import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { EPaymentMethods, IAdyenSettings } from '@model';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';

import { APPLE_PAY_SERVICE, GOOGLE_PAY_SERVICE } from '../constants';
import { IAdyenInitConfiguration, TApplePayService, TGooglePayService } from '../model';

import { useAdyenLib } from './useAdyenLib';

export const usePaymentAvailability = (clientKey: string, adyenPaymentSettings: IAdyenSettings) => {
  const [googlePayAvailable, setGooglePayAvailable] = useState<boolean>(false);
  const [applePayAvailable, setApplePayAvailable] = useState<boolean>(false);

  const {
    applicationSettings: {
      localization: { languageIdentifier: locale },
    },
  } = useApplicationRuntimeContext();
  const {
    settings: { environment },
  } = useApplicationBuildContext();

  const configuration: IAdyenInitConfiguration = useMemo(
    () => ({ locale, clientKey, environment }),
    [locale, clientKey, environment],
  );
  const adyenLib = useAdyenLib({
    configuration,
    availablePaymentMethods: { paymentMethods: [], storedPaymentMethods: [] },
  });

  useEffect(() => {
    const isAvailable = async ({
      paymentMethod,
      cbResult,
    }: {
      paymentMethod: TGooglePayService | TApplePayService;
      cbResult: (result: boolean) => void;
    }) => {
      if (!adyenLib) {
        return;
      }

      if (paymentMethod === 'googlepay') {
        const payment = new GooglePay(adyenLib, {
          configuration: {
            merchantId: adyenPaymentSettings.googlePayMerchantId ?? '',
            gatewayMerchantId: adyenPaymentSettings.merchantAccount,
            merchantName: adyenPaymentSettings.googlePayMerchantName,
          },
        });
        await payment
          .isAvailable()
          .then(() => {
            cbResult(true);
          })
          .catch(() => {
            cbResult(false);
          });
      }

      if (paymentMethod === 'applepay') {
        const payment = new ApplePay(adyenLib, {
          configuration: {
            merchantId: adyenPaymentSettings.applePayMerchantId,
          },
        });
        await payment
          .isAvailable()
          .then(() => {
            cbResult(true);
          })
          .catch(() => {
            cbResult(false);
          });
      }
    };

    nonAwaitedPromiseHandlerParametrized(isAvailable, {
      paymentMethod: GOOGLE_PAY_SERVICE,
      cbResult: setGooglePayAvailable,
    });
    nonAwaitedPromiseHandlerParametrized(isAvailable, {
      paymentMethod: APPLE_PAY_SERVICE,
      cbResult: setApplePayAvailable,
    });
  }, [setGooglePayAvailable, setApplePayAvailable, adyenLib]);

  return useCallback(
    (paymentMethod: string | null): boolean => {
      switch (paymentMethod) {
        case EPaymentMethods.APPLE_PAY.toString():
          return applePayAvailable;
        case EPaymentMethods.GOOGLE_PAY.toString():
          return googlePayAvailable;
        default:
          return true;
      }
    },
    [googlePayAvailable, applePayAvailable],
  );
};
